import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ZoomIn, ZoomOut, Maximize2, Printer, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { orgchartApi, masterApi, employeeApi } from '../lib/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useUnit } from '../contexts/UnitContext';
import { useAuth } from '../contexts/AuthContext';
import { displayDepartment } from '../lib/utils';
import { captureChart, chartCanvasToPdf } from '../lib/chartPdf';

const CARD_W = 230, CARD_H = 104, GAP_X = 50, ROW_H = 180;
// The department every other department reports to. The chart draws the other
// six beneath it; see where it is used.
const MANAGEMENT = 'Management';
const PALETTE = ['#2563EB', '#15A36E', '#7C6BD6', '#E9A23B', '#0E6B8C', '#A23A6E', '#137A52', '#A35418'];

export default function DeptChart() {
  const { activeUnit, isAllUnits } = useUnit();
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(0.85);
  const viewRef = useRef(null);
  const contentRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const [departments, setDepartments] = useState([]);
  // Assigning a section owner: { deptId, dept, section, unit, employee_id }
  const [ownerEdit, setOwnerEdit] = useState(null);
  const [unitStaff, setUnitStaff] = useState([]);
  const canManage = hasPerm('master.manage');

  const loadDepartments = () =>
    masterApi.departments().then((r) => setDepartments(r.data || [])).catch(() => {});

  // A section is owned per unit, so the person can only be picked from the
  // unit being viewed — naming somebody for Unit 1 must never move a Unit 3
  // employee into Unit 1.
  const openOwnerEdit = async (box, section) => {
    const row = (departments || []).find((d) => d.name === box.dept);
    if (!row || isAllUnits || !activeUnit) return;
    const current = (section.owners || []).find((o) => o.unit === activeUnit);
    setOwnerEdit({ deptId: row.id, dept: box.dept, section: section.name,
                   unit: activeUnit, employee_id: current?.owner_id || '' });
    try {
      const r = await employeeApi.list({ unit: activeUnit, limit: 1000 });
      const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setUnitStaff(rows.filter((e) => e.status !== 'Left')
        .sort((a, b) => (b.level || 0) - (a.level || 0)
          || String(a.name || '').localeCompare(String(b.name || ''))));
    } catch { setUnitStaff([]); }
  };

  const saveOwner = async () => {
    if (!ownerEdit) return;
    try {
      await masterApi.setSectionOwner(ownerEdit.deptId, ownerEdit.section,
                                      ownerEdit.unit, ownerEdit.employee_id);
      toast.success('Saved');
      setOwnerEdit(null);
      await loadDepartments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeUnit]);
  useEffect(() => {
    loadDepartments();
    /* eslint-disable-next-line */
  }, []);
  const load = async () => {
    setLoading(true);
    // all_levels: this chart is about DEPARTMENTS — their headcount and who
    // heads them — so it counts everybody. The level-visibility rule applies to
    // the organisational chart, where the junior grades are not drawn; leaving
    // them out here would understate how big a department is.
    try { const r = await orgchartApi.get({ unit: activeUnit, all_levels: true }); setNodes(r.data); }
    catch (e) { toast.error('Failed to load chart'); }
    finally { setLoading(false); }
  };

  // Build the chart from the Departments master, and put this unit's people on
  // it. The master is the structure — seven departments, each sub-unit inside
  // one of them — so a department with nobody in this unit still gets its box
  // (FG Store is empty today and is still part of the structure).
  const deptNodes = useMemo(() => {
    const allById = Object.fromEntries(nodes.map((n) => [n.id, n]));
    const real = nodes.filter((n) => !n.is_vacant && !n.org_only);
    // The name the box will CARRY, not the one stored on the person. An ALIAS
    // spelling resolves to its department — QUALITY CONTROL, QUALITY ASSURANCE,
    // SQA and Standard Room are four stored values and one Quality box — while
    // a sub-unit keeps its own name and gets its own box inside its department.
    const disp = (d) => displayDepartment(d || '—', departments) || '—';
    const byDept = {};
    real.forEach((n) => { const d = disp(n.department); (byDept[d] = byDept[d] || []).push(n); });

    // Every master row that is a department or a sub-unit. Aliases are skipped:
    // they are the same box as their parent, already merged by disp() above.
    const rows = (departments || []).filter((d) => d.name && !d.alias);
    const named = new Set(rows.map((d) => d.name));
    const parentOf = {};
    rows.forEach((d) => {
      const parent = d.parent && named.has(d.parent) ? d.parent : null;
      if (parent && parent !== d.name) parentOf[d.name] = parent;
    });

    const boxFor = (dept) => {
      const members = byDept[dept] || [];
      // entry points = members whose manager is outside this department (or none)
      const entries = members.filter((m) => {
        const mgr = allById[m.reporting_to];
        return !mgr || disp(mgr.department) !== dept;
      });
      const head = (entries.length ? entries : members)
        .slice().sort((a, b) => (b.level || 0) - (a.level || 0))[0] || null;
      return { id: dept, dept, head, headcount: members.length, parent: parentOf[dept] || null };
    };
    // `sections` are areas of responsibility inside a department — who handles
    // what — not sub-units. They are drawn as lines in the department's own box
    // and never as boxes of their own; see the card below.
    const out = rows.map((d) => ({ ...boxFor(d.name), sections: d.sections || null }));
    // A department somebody typed that the master has never heard of still gets
    // a box — losing people off the chart would be worse than an odd box. Its
    // parent is guessed from where its head reports, as the whole chart used to.
    Object.keys(byDept).filter((d) => !named.has(d)).forEach((d) => {
      const box = boxFor(d);
      const mgr = box.head ? allById[box.head.reporting_to] : null;
      const derived = mgr && mgr.department && disp(mgr.department) !== d ? disp(mgr.department) : null;
      out.push({ ...box, parent: derived, unknown: true });
    });

    // Management sits above the other six departments. Drawn here rather than
    // recorded as a parent in the master, because a parent there also means
    // "reads as" — making Management the parent of everything would label all
    // 354 people "Management" on every screen that names a department.
    const byId = Object.fromEntries(out.map((d) => [d.id, d]));
    const top = out.find((d) => d.dept === MANAGEMENT && !d.parent);
    if (top) {
      out.forEach((d) => {
        if (d !== top && !(d.parent && byId[d.parent])) { d.parent = MANAGEMENT; d.reportsUp = true; }
      });
    }

    // What a department covers: itself plus every sub-unit inside it, at any
    // depth. Management is the exception — the six departments under it are
    // drawn there, not booked to it, so counting them in would make its badge
    // read as the whole company.
    const totalOf = (d, seen = new Set()) => {
      if (seen.has(d.id)) return 0;
      seen.add(d.id);
      return d.headcount + out.filter((c) => c.parent === d.dept && !c.reportsUp)
        .reduce((s, c) => s + totalOf(c, seen), 0);
    };
    out.forEach((d) => {
      const inside = out.filter((c) => c.parent === d.dept && !c.reportsUp);
      if (inside.length) { d.isGroup = true; d.groupHeadcount = totalOf(d); }
      // One of the seven: at the top of the structure, or drawn under
      // Management only because everything reports there.
      d.isDepartment = !d.parent || d.reportsUp;
    });
    return out;
  }, [nodes, departments]);

  const { positioned, edges, width, height } = useMemo(() => {
    const byId = Object.fromEntries(deptNodes.map((d) => [d.id, d]));
    const children = {};
    deptNodes.forEach((d) => { const p = (d.parent && byId[d.parent]) ? d.parent : null; (children[p] = children[p] || []).push(d); });
    const roots = children[null] || [];
    const depthOf = (id, seen) => { const d = byId[id]; if (!d || !d.parent || !byId[d.parent] || seen.has(id)) return 0; seen.add(id); return 1 + depthOf(d.parent, seen); };
    const xOrder = {}; let cursor = 0;
    const assignX = (id, guard) => {
      if (guard.has(id)) return; guard.add(id);
      const kids = (children[id] || []);
      if (!kids.length) { xOrder[id] = cursor; cursor += 1; return; }
      kids.forEach((k) => assignX(k.id, guard));
      xOrder[id] = kids.reduce((s, k) => s + (xOrder[k.id] || 0), 0) / kids.length;
    };
    roots.forEach((r) => assignX(r.id, new Set()));
    deptNodes.forEach((d) => { if (xOrder[d.id] == null) { xOrder[d.id] = cursor; cursor += 1; } });
    const positioned = deptNodes.map((d, i) => ({
      ...d, color: PALETTE[i % PALETTE.length],
      x: (xOrder[d.id] || 0) * (CARD_W + GAP_X),
      y: depthOf(d.id, new Set()) * ROW_H,
    }));
    const pById = Object.fromEntries(positioned.map((d) => [d.id, d]));
    const edges = positioned.filter((d) => pById[d.parent]).map((d) => ({ from: pById[d.parent], to: d }));
    const maxDepth = Math.max(0, ...positioned.map((d) => d.y / ROW_H));
    const width = Math.max(...positioned.map((d) => d.x), 0) + CARD_W + GAP_X;
    const height = (maxDepth + 1) * ROW_H + 40;
    return { positioned, edges, width, height };
  }, [deptNodes]);

  const fit = () => { const vw = viewRef.current?.clientWidth || 1000; setZoom(Math.max(0.3, Math.min(vw / (width || 1), 1))); };

  // The same sheet the org chart produces — controlled-document header, whole
  // chart at natural size on one page, sign-off footer. Shared so a change to
  // the document furniture lands on both; see lib/chartPdf.
  const exportPdf = async () => {
    if (exporting) return;
    setExporting(true);
    toast.message('Preparing PDF…', { description: 'Rendering the full chart' });
    try {
      const canvas = await captureChart({
        content: contentRef.current,
        wrapper: contentRef.current?.parentElement,
        scrollView: viewRef.current,
        width,
        height,
        label: 'DeptChart',
      });
      await chartCanvasToPdf(canvas, `Department Chart — ${activeUnit || 'All Units'}`);
    } catch (e) {
      console.error('[DeptChart PDF] failed', e);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };
  useEffect(() => { if (!loading && deptNodes.length) fit(); /* eslint-disable-next-line */ }, [loading, width]);

  return (
    <div data-testid="dept-chart-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 flex flex-col overflow-hidden p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-[#11182B]">Department Chart</h1>
              {/* Departments, not boxes: a sub-unit is drawn inside the
                  department it belongs to and is not one of itself. */}
              <p className="text-sm text-[#5A6377]">
                {activeUnit || 'All'} · {deptNodes.filter((d) => !d.parent || d.reportsUp).length} departments
                {' · '}headcount is for this unit · click a box for its org chart
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setZoom((z) => Math.max(0.3, z - 0.1))}><ZoomOut className="w-4 h-4" /></Button>
              <Button variant="outline" onClick={fit}><Maximize2 className="w-4 h-4 mr-1" />Fit</Button>
              <Button variant="outline" onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))}><ZoomIn className="w-4 h-4" /></Button>
              <Button variant="outline" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" />Print</Button>
              <Button data-testid="dept-export-pdf" variant="outline" onClick={exportPdf} disabled={exporting || loading || !deptNodes.length}>
                <FileDown className="w-4 h-4 mr-1" />{exporting ? 'Preparing…' : 'PDF'}
              </Button>
            </div>
          </div>

          {loading ? <div className="text-sm text-[#5A6377]">Loading…</div> : deptNodes.length === 0 ? (
            <div className="text-sm text-[#98A1B2]">No departments in this unit.</div>
          ) : (
            <div ref={viewRef} className="flex-1 overflow-auto border border-[#E6E9F0] rounded-[14px] bg-white">
              <div style={{ width: width * zoom, height: height * zoom }}>
                <div ref={contentRef} style={{ width, height, transform: `scale(${zoom})`, transformOrigin: 'top left', position: 'relative' }}>
                  <svg width={width} height={height} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    <defs>
                      <marker id="darrow" markerWidth="10" markerHeight="10" refX="6" refY="3" orient="auto" markerUnits="strokeWidth">
                        <path d="M0,0 L6,3 L0,6 Z" fill="#102468" />
                      </marker>
                    </defs>
                    {edges.map((e, i) => {
                      const x1 = e.from.x + CARD_W / 2, y1 = e.from.y + CARD_H;
                      const x2 = e.to.x + CARD_W / 2, y2 = e.to.y;
                      const my = (y1 + y2) / 2;
                      return <path key={i} d={`M ${x1} ${y1} V ${my} H ${x2} V ${y2}`} stroke="#102468" strokeWidth="1.75" fill="none" markerEnd="url(#darrow)" />;
                    })}
                  </svg>
                  {positioned.map((d) => (
                    <div key={d.id} data-testid={`dept-box-${d.dept}`} onClick={() => navigate(`/orgchart?department=${encodeURIComponent(d.dept)}`)}
                      style={{ position: 'absolute', left: d.x, top: d.y, width: CARD_W,
                               borderTop: `${d.isDepartment ? 4 : 2}px solid ${d.color}` }}
                      className="rounded-[12px] p-4 shadow-sm border border-[#E6E9F0] bg-white text-center cursor-pointer hover:shadow-md transition-shadow"
                      title={d.isDepartment ? 'Open this department\'s employees'
                                            : `Inside ${d.parent} — open its employees`}>
                      <div className={`leading-tight text-[#11182B] ${d.isDepartment ? 'text-sm font-bold' : 'text-[13px] font-semibold'}`}>
                        {/* Already the display name: the boxes are grouped by
                            it, so there is nothing left to translate here.

                            Every box is a live part of the organisation, so
                            every box is white. Rank is carried by the title
                            weight and the thickness of the coloured rule on
                            top — a greyed fill read as "dull", which is what a
                            disabled or retired department should look like and
                            none of these are. */}
                        {d.dept}
                      </div>
                      <div className="mt-2 text-[11px] text-[#5A6377]">
                        {/* A department split into areas of responsibility
                            lists them here — section on the left, the person
                            who handles it on the right. One box, not one box
                            per section: three sub-units of one person each read
                            as three departments, which is not what they are.

                            Otherwise the senior-most person in the department,
                            named without a label: the box is the department, so
                            a name under its title needs no introducing. */}
                        {d.sections?.length ? (
                          <div className="space-y-1 text-left">
                            {d.sections.map((s) => {
                              // A section is owned PER UNIT. Viewing one unit
                              // shows that unit's owner and NOTHING ELSE: Unit
                              // 3's people are not Unit 1's answer, and an
                              // empty slot is the honest reading until somebody
                              // fills it. Viewing all units shows each of them.
                              const owners = s.owners || [];
                              const mine = isAllUnits ? owners
                                : owners.filter((o) => !o.unit || o.unit === activeUnit);
                              const editable = canManage && !isAllUnits && activeUnit;
                              return (
                                <div key={s.name} className="flex items-start justify-between gap-2">
                                  <span className="text-[9px] uppercase tracking-wide text-[#98A1B2] leading-tight">{s.name}</span>
                                  {mine.length ? (
                                    <span
                                      onClick={editable ? (e) => { e.stopPropagation(); openOwnerEdit(d, s); } : undefined}
                                      title={editable ? 'Change who owns this section in this unit' : undefined}
                                      className={`text-[10px] font-semibold text-[#11182B] whitespace-nowrap leading-tight text-right ${editable ? 'hover:underline' : ''}`}>
                                      {mine.map((o) => o.owner).join(' · ')}
                                    </span>
                                  ) : (
                                    // The space to write in. Dashed rather than
                                    // blank so it reads as a slot nobody has
                                    // filled, not as a rendering slip.
                                    <span
                                      data-testid={`section-unassigned-${d.dept}-${s.name}`}
                                      onClick={editable ? (e) => { e.stopPropagation(); openOwnerEdit(d, s); } : undefined}
                                      title={editable ? `Name who owns this in ${activeUnit}` : 'Nobody recorded for this unit'}
                                      className={`text-[10px] leading-tight min-w-[72px] border-b border-dashed border-[#C7CEDB] text-[#98A1B2] text-right ${editable ? 'cursor-pointer hover:border-[#15307F] hover:text-[#15307F]' : ''}`}>
                                      {editable ? 'assign' : '—'}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : d.head ? <>{d.head.name}<div className="text-[10px] text-[#98A1B2]">{d.head.designation}{d.head.level ? ` · L${d.head.level}` : ''}</div></> : <span className="text-[#98A1B2]">Not assigned</span>}
                      </div>
                      <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#15307F] bg-[#E8EFFD] px-2 py-0.5 rounded-full">
                        {/* A group counts everything inside it; the number in
                            brackets is what is booked to the group itself. */}
                        <Users className="w-3 h-3" />
                        {d.isGroup && d.groupHeadcount != null ? d.groupHeadcount : d.headcount}
                        {d.isGroup && d.headcount ? ` (${d.headcount} direct)` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Naming the person who owns a section IN THIS UNIT. The list is this
          unit's staff only — the point of a per-unit owner is that recording
          one never moves anybody between units. */}
      <Dialog open={!!ownerEdit} onOpenChange={(o) => { if (!o) setOwnerEdit(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {ownerEdit?.section} — {ownerEdit?.unit}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[#5A6377]">
              Who owns {ownerEdit?.section} in {ownerEdit?.unit}? Other units keep their own owner.
            </p>
            <select
              data-testid="section-owner-select"
              value={ownerEdit?.employee_id || ''}
              onChange={(e) => setOwnerEdit({ ...ownerEdit, employee_id: e.target.value })}
              className="w-full border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-1.5">
              <option value="">— nobody yet —</option>
              {unitStaff.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}{e.designation ? ` · ${e.designation}` : ''}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOwnerEdit(null)}>Cancel</Button>
              <Button data-testid="section-owner-save" onClick={saveOwner}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
